








import { Component, Prop, Vue } from 'vue-property-decorator';
import { VNode } from 'vue';
import * as d3 from 'd3';
import { Chart, ToothState } from '@/model/chart';

@Component({
    components: {
        // Small functional component that creates either a 'path' or 'polygon' element
        Shape: Vue.extend({
            functional: true,
            render(h, {data, props}): VNode {
                const el = { ...data };
                el.attrs = { ...el.attrs, ...props };

                return h(el.attrs.d ? 'path' : 'polygon', el);
            },
        }),
    },
})
export default class Tooth extends Vue {
    @Prop({ type: Number, required: true }) private fdi!: number;
    @Prop({ required: true }) private textTransform?: string;
    @Prop() private points?: string;
    @Prop() private d?: string;
    @Prop({ required: true, type: Chart }) private chart!: Chart;

    @Prop({ type: Boolean, default: false }) private showLabel!: boolean;
    private isMounted = false;

    get boundingBox() {
        const polygons = this.$el.getElementsByTagName('polygon');

        if (polygons?.length === 1) {
            return polygons[0].getBBox();
        }

        return this.$el.getElementsByTagName('path')[0].getBBox();
    }

    get state() {
        return this.tooth.state;
    }

    get tooth() {
        return this.chart.getToothByFdi(this.fdi);
    }

    get quadrant(): number {
        return this.tooth.quadrantCode;
    }

    get number(): number {
        return this.tooth.toothCode;
    }

    get centroid() {
        if (!this.$refs.shape) {
            return undefined;
        }

        const points: Array<[number, number]> = [];
        if (this.d) {
            const path = this.$refs.shape as SVGPathElement;
            const length = path.getTotalLength();
            for (let l = 0; l <= length; l += length / 50) {
                const p = path.getPointAtLength( Math.min(l, length) );
                points.push([p.x, p.y]);
            }
        } else {
            const polygon = this.$refs.shape as SVGPolygonElement;
            for (let i = 0; i < polygon.points?.length ?? 0; ++i) {
                const p = polygon.points.getItem(i);
                points.push([p.x, p.y]);
            }
        }

        return d3.polygonCentroid(points);
    }

    get iconName() {
        switch (this.state) {
            case ToothState.MovedLeft:
                if (this.quadrant % 2 !== 0) {
                    return 'movedright';
                }
                break;

            case ToothState.MovedRight:
                if (this.quadrant % 2 !== 0) {
                    return 'movedleft';
                }
                break;
        }

        return this.stateClass;
    }

    get iconTransform() {
        const moveTarget = this.chart.getMoveTarget(this.tooth);
        if (!moveTarget) {
            return undefined;
        }

        // If a tooth has moved, we want to orient the icon so that it points to the target spot
        // For this, we need two vectors: source (vs) and target(vt).
        // The source vector represents original orientation of the icon (x-aligned), and the target vector is
        // the vector between the centroids of the teeth
        //
        // We calculate the angle between vs and vt, and rotate the icon by that much.
        // This is a bit complicated by the fact that we use two icons, with two different orientation, but also by the
        // fact that "left" and "right" have different meanings in different quadrants
        const app = this.$root.$children[0] as any;
        const targetPoint = (app.getTooth(moveTarget.fdi) as Tooth).centroid!;
        const sourcePoint = (app.getTooth(this.fdi) as Tooth).centroid!;

        // Source vector
        // Because we use different, flipped, icons for left and right movement, we have to choose the source vector
        // differently, depending on which icon is used.
        // The source vector is always either (1, 0) or (-1, 0) depending on the icon orientation
        const vs = {
            x:  this.quadrant % 2 === 0
                    ? (this.tooth.toothCode > moveTarget.toothCode ? -1 : 1)
                    : (this.tooth.toothCode > moveTarget.toothCode ? 1 : -1),
            y: 0,
        };

        // Target vector, pointing from source centroid to target centroid
        const vt = {
            x: targetPoint[0] - sourcePoint[0],
            y: targetPoint[1] - sourcePoint[1],
        };

        // Compute the angle between vs and vt, according to formula:
        //                      vs * vt
        //      cos(angle) = ----------------
        //                     |vs| * |vt|
        //
        const dot = vs.x * vt.x + vs.y * vt.y;
        const ns = Math.sqrt(vs.x * vs.x + vs.y * vs.y);
        const nt = Math.sqrt(vt.x * vt.x + vt.y * vt.y);
        const angleRadians = Math.acos( dot / (ns * nt) );

        // In the left-most quadrants, the icon actually needs to rotate the other way
        const sign = this.quadrant === 1 || this.quadrant === 4 ? -1 : 1;
        const angleDegrees = sign * angleRadians * 180 / Math.PI;

        return `rotate(${angleDegrees.toFixed(2)} ${this.centerX} ${this.centerY})`;
    }

    get centerX() { return this.centroid ? this.centroid[0] : 0; }
    get centerY() { return this.centroid ? this.centroid[1] : 0; }

    public mounted() {
        this.isMounted = true;
    }

    private get stateClass() {
        return ToothState[this.state].toLowerCase();
    }

    private onClick() {
        this.$appInsights.trackEvent({ name: 'ToothClick', properties: { fdi: this.fdi }});
        const allStates = this.chart.getAllowedStates(this.tooth);
        const stateIndex = allStates.indexOf(this.tooth.state);
        const newIndex = (stateIndex + 1) % allStates.length;
        this.chart.setState(this.tooth, allStates[newIndex]);
    }
}
